import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { History, RotateCcw, Ruler, Check, Scale, Calculator } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const norm = (s) => (s || "").replace(/^(Reused:\s*)+/i, "").trim() || "Manual";

const META = {
  "Blueprint Takeoff": { tone: "text-brandred", ring: "border-brandred/40", bg: "bg-brandred/5", label: "Blueprint" },
  "Satellite Measure": { tone: "text-navy", ring: "border-navy/40", bg: "bg-navy/5", label: "Satellite" },
  "AI Aerial Estimate": { tone: "text-amberw", ring: "border-amberw/40", bg: "bg-amberw/5", label: "AI Aerial" },
  "Manual": { tone: "text-slate-500", ring: "border-slate-300", bg: "bg-slate-50", label: "Manual" },
};
const ORDER = ["Blueprint Takeoff", "Satellite Measure", "AI Aerial Estimate", "Manual"];

export default function MeasurementHistory({ propertyId, contactId, onReused }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const nav = useNavigate();

  const load = () =>
    api.get(`/measurements?property_id=${propertyId}`)
      .then((r) => setRows((r.data || []).slice().reverse()))  // newest first
      .catch(() => {});
  useEffect(() => { if (propertyId) load(); }, [propertyId]); // eslint-disable-line

  const reuse = async (m) => {
    setBusy(m.id);
    try {
      const base = norm(m.source);
      await api.post("/takeoff/manual", { property_id: propertyId, squares: m.squares, pitch: m.pitch, source: `Reused: ${base}` });
      toast.success(`Now using ${m.squares} squares (${META[base]?.label || base}) for this property`);
      load(); onReused?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not re-use measurement"); }
    finally { setBusy(""); }
  };

  // Make the measurement active, then jump into the estimator to price it.
  const priceThis = async (m, isActive) => {
    setBusy(m.id);
    try {
      if (!isActive) {
        const base = norm(m.source);
        await api.post("/takeoff/manual", { property_id: propertyId, squares: m.squares, pitch: m.pitch, source: `Reused: ${base}` });
        onReused?.();
      }
      if (contactId) nav(`/estimating?contact=${contactId}`);
      else toast.error("No customer linked to this property");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open estimator"); setBusy(""); }
  };

  if (!rows.length) return null;

  // Best (latest) measurement per source category, in display order
  const byCat = {};
  for (const m of rows) { const c = norm(m.source); if (!byCat[c]) byCat[c] = m; }
  const cats = ORDER.filter((c) => byCat[c]).concat(Object.keys(byCat).filter((c) => !ORDER.includes(c)));
  const activeCat = norm(rows[0].source); // estimate consumes the latest measurement

  return (
    <div className="mt-3" data-testid={`measure-history-${propertyId}`}>
      {cats.length >= 2 && (
        <>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><Scale className="w-3 h-3" /> Compare measurements</div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
            {cats.map((c) => {
              const m = byCat[c]; const meta = META[c] || META.Manual; const active = c === activeCat;
              const slug = c.replace(/\s+/g, "-").toLowerCase();
              return (
                <div key={c} data-testid={`compare-card-${slug}`}
                  className={`relative rounded-xl border-2 p-2.5 ${active ? "border-navy shadow-md " + meta.bg : `${meta.ring} ${meta.bg}`}`}>
                  {active && <span className="absolute -top-2 right-2 text-[9px] font-bold uppercase bg-navy text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5" data-testid={`active-${slug}`}><Check className="w-2.5 h-2.5" /> In use</span>}
                  <div className={`text-[10px] font-bold uppercase tracking-wide flex items-center gap-1 ${meta.tone}`}><Ruler className="w-3 h-3" /> {meta.label}</div>
                  <div className="font-num text-2xl font-extrabold text-navy leading-tight mt-0.5">{m.squares}<span className="text-xs font-semibold text-slate-400"> sq</span></div>
                  <div className="text-[11px] text-slate-400 font-num">{m.pitch} · {m.created_at ? fmtDate(m.created_at) : ""}</div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <button onClick={() => priceThis(m, active)} disabled={busy === m.id} data-testid={`price-measure-${slug}`}
                      className="text-[11px] font-bold text-white bg-brandred rounded-md px-2 py-0.5 hover:brightness-110 disabled:opacity-50 flex items-center gap-1"><Calculator className="w-3 h-3" /> Price this</button>
                    {active ? (
                      <span className="text-[11px] font-semibold text-navy flex items-center gap-1"><Check className="w-3 h-3" /> In use</span>
                    ) : (
                      <button onClick={() => reuse(m)} disabled={busy === m.id} data-testid={`use-measure-${slug}`}
                        className="text-[11px] font-bold text-navy hover:underline disabled:opacity-50">Use this</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1"><History className="w-3 h-3" /> All measurements</div>
      <div className="space-y-1">
        {rows.map((m) => {
          const meta = META[norm(m.source)] || META.Manual;
          return (
            <div key={m.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5" data-testid={`measure-row-${m.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Ruler className={`w-3.5 h-3.5 shrink-0 ${meta.tone}`} />
                <span className="font-num font-bold text-navy">{m.squares} sq</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500 font-num">{m.pitch}</span>
                <span className="text-slate-300 truncate">· {m.source}{m.created_at ? ` · ${fmtDate(m.created_at)}` : ""}</span>
              </div>
              <button onClick={() => reuse(m)} disabled={busy === m.id} data-testid={`reuse-measure-${m.id}`}
                className="shrink-0 text-navy font-semibold flex items-center gap-1 hover:text-brandred disabled:opacity-50">
                <RotateCcw className="w-3 h-3" /> Re-use
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
